/**
 * Trial Balance — /finance/reports/trial-balance
 *
 * Shows debit / credit totals per GL account for a chosen period.
 * Highlights when totals are not balanced (Dr ≠ Cr).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3, RefreshCw, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadAuthenticatedFile } from './report-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TbAccount {
  accountId: string | null;
  accountCode: string;
  accountName: string;
  accountType: string;
  normalBalance: string;
  debit: number;
  credit: number;
  net: number;
}

interface TrialBalanceData {
  reportType: string;
  period: { from: string; to: string };
  accounts: TbAccount[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
  balanced: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  asset:     'bg-blue-50 text-blue-700',
  liability: 'bg-orange-50 text-orange-700',
  equity:    'bg-purple-50 text-purple-700',
  revenue:   'bg-green-50 text-green-700',
  expense:   'bg-red-50 text-red-700',
};

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear(): { from: string; to: string } {
  const now   = new Date();
  const year  = now.getFullYear();
  const today = now.toISOString().split('T')[0]!;
  return { from: `${year}-01-01`, to: today };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TrialBalancePage() {
  const [loading, setLoading]     = useState(true);
  const [data, setData]           = useState<TrialBalanceData | null>(null);
  const [from, setFrom]           = useState(thisYear().from);
  const [to, setTo]               = useState(thisYear().to);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      const res = await api.get<TrialBalanceData>(`/finance/reports/trial-balance?from=${f}&to=${t}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    fetchData(from, to);
  }, [fetchData, from, to]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to);
  }, [fetchData, from, to]);

  const accounts = (data?.accounts ?? []).filter(a => {
    if (typeFilter !== 'all' && a.accountType !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!a.accountCode.toLowerCase().includes(q) && !a.accountName.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const accountTypes = ['all', ...Array.from(new Set((data?.accounts ?? []).map(a => a.accountType).filter(Boolean)))];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold">Trial Balance</h1>
            <p className="text-sm text-muted-foreground">Debit / credit totals by GL account</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <input type="date" value={to} onChange={e => { setTo(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadAuthenticatedFile(`/finance/reports/trial-balance/export.xlsx?from=${from}&to=${to}`, `trial-balance-${from}-${to}.xlsx`)}>
            <Download className="h-4 w-4 mr-2" /> XLSX
          </Button>
        </div>
      </div>

      {/* Balance banner */}
      {data && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
          data.balanced ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {data.balanced
            ? <><CheckCircle2 className="h-4 w-4" /> Balanced — Total Dr = Total Cr = {fmt(data.totalDebit)}</>
            : <><AlertCircle className="h-4 w-4" /> UNBALANCED — Difference: {fmt(Math.abs(data.difference))} (Dr {fmt(data.totalDebit)} / Cr {fmt(data.totalCredit)})</>}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Input placeholder="Search account..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-48 h-8 text-sm" />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border rounded px-2 py-1 text-sm">
          {accountTypes.map(t => <option key={t} value={t}>{t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Net (Dr−Cr)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && accounts.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No accounts found for this period.</TableCell></TableRow>
                )}
                {accounts.map(a => (
                  <TableRow key={a.accountId ?? a.accountCode} className="border-t hover:bg-muted/30">
                    <TableCell className="font-mono text-xs">{a.accountCode}</TableCell>
                    <TableCell>{a.accountName}</TableCell>
                    <TableCell>
                      <Badge className={TYPE_COLORS[a.accountType] ?? 'bg-gray-100 text-gray-700'}>
                        {a.accountType || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmt(a.debit)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(a.credit)}</TableCell>
                    <TableCell className={`px-4 py-2.5 text-right font-mono font-medium ${a.net >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                      {fmt(a.net)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {data && accounts.length > 0 && (
                <tfoot className="bg-muted/70 font-semibold">
                  <TableRow className="border-t-2 border-border">
                    <TableCell colSpan={3}>Total ({data.accounts.length} accounts)</TableCell>
                    <TableCell className="text-right font-mono">{fmt(data.totalDebit)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(data.totalCredit)}</TableCell>
                    <TableCell className={`px-4 py-3 text-right font-mono ${data.balanced ? 'text-green-700' : 'text-red-600'}`}>
                      {data.balanced ? '✓ 0.00' : fmt(data.difference)}
                    </TableCell>
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

